/**
 * src/data/growthPoints.js
 *
 * The single source of truth for the GrowthPoints offer: the four monthly
 * plans, the five one-off blocks, and the full point catalog.
 *
 * WHY IT IS ONE FILE
 *
 * The same numbers appear on the homepage plan preview, the GrowthPoints sales
 * page, the point catalog, the application's investment mapping and the
 * example allocations. When those lived in each page, a price could be
 * corrected in one place and stay wrong in three others. Everything reads from
 * here instead, and the example allocations below are checked against the
 * catalog by scripts/test_growth_points.mjs — a typo in a worked example that
 * no longer sums to its plan total fails the test rather than shipping.
 *
 * Source: Site Rebranding/uploads/02-growth-points-sales-page.md
 *         Site Rebranding/uploads/06-growth-points-catalog.md
 *
 * ON PRICES
 *
 * `priceMonthly` is what the plan actually costs, in whole dollars. It is NOT
 * rendered on the public pages: the funnel qualifies first and quotes on the
 * Growth Allocation Call, so the plan cards show points and slots and the CTA
 * is an application. The figures live here because the application's
 * investment bands are derived from them (see INVESTMENT_BANDS), and a band
 * that no longer lines up with a real price would route people to a plan they
 * cannot afford.
 */

/** Monthly plans. `slots` is production velocity; `points` is total volume. */
export const PLANS = [
  {
    id: "starter-growth",
    name: "Starter Growth",
    points: 25,
    slots: 2,
    priceMonthly: 2500,
    best: "Best for a small established business that needs consistent design, content, marketing support, basic website or SEO updates, and monthly reporting.",
    includes: [
      "Light monthly strategy",
      "Social, content, or design support",
      "Basic website or SEO updates",
      "Monthly reporting",
    ],
    featured: false,
  },
  {
    id: "momentum",
    name: "Momentum",
    points: 45,
    slots: 3,
    priceMonthly: 4500,
    best: "Best for a business ready to coordinate content, social, email, SEO, landing pages, and campaign optimization.",
    includes: [
      "Content marketing",
      "Social campaigns",
      "Email campaigns",
      "SEO support",
      "Landing page or campaign optimization",
    ],
    featured: false,
  },
  {
    id: "scale-engine",
    name: "Scale Engine",
    points: 75,
    slots: 5,
    priceMonthly: 7500,
    best: "Best for a business actively scaling campaigns, leads, automation, landing pages, email sequences, SEO content, and conversion.",
    includes: [
      "Campaign strategy",
      "Paid ads support",
      "Landing pages",
      "Email sequences",
      "SEO content",
      "Automation support",
      "Conversion optimization",
    ],
    featured: true,
  },
  {
    id: "authority",
    name: "Authority",
    points: 110,
    slots: 7,
    priceMonthly: 11000,
    best: "Best for brands that need deeper strategy, multi-channel execution, authority-building content, AI-assisted workflows, automation, reporting, and optimization.",
    includes: [
      "Full-funnel marketing support",
      "Advanced campaign execution",
      "Authority-building content",
      "AI-assisted workflows",
      "Automation support",
      "Reporting and optimization",
    ],
    featured: false,
  },
];

/**
 * One-off blocks, for a sprint without a monthly plan or extra capacity
 * alongside one. Priced at a higher point rate because they reserve no
 * long-term capacity.
 */
export const BLOCKS = [
  { id: "quick-win",    name: "Quick Win Block", points: 10,  price: 1250,  best: "Small fixes, minor web updates, mini audits, and quick improvements." },
  { id: "project",      name: "Project Block",   points: 25,  price: 3000,  best: "A focused landing-page component, content batch, setup, or automation cleanup." },
  { id: "campaign",     name: "Campaign Block",  points: 50,  price: 5750,  best: "Funnel setup, campaign push, or SEO/content sprint." },
  { id: "growth-sprint", name: "Growth Sprint",  points: 75,  price: 8250,  best: "A launch window, larger build, automation project, or AI workflow." },
  { id: "launch",       name: "Launch Block",    points: 100, price: 10500, best: "Major launches, website projects, full funnels, or custom growth initiatives." },
];

/**
 * The complete point catalog, by discipline.
 *
 * `points` is the number the calculator and the example allocations use. Where
 * the published value is a range or a per-unit rate, `points` is the lower
 * bound and `display` carries the real label — so "21–34" contributes 21 to a
 * total but never renders as a figure the client did not publish.
 */
export const CATALOG = [
  {
    id: "strategy",
    name: "Strategy and planning",
    items: [
      { name: "Monthly marketing plan", points: 5 },
      { name: "Campaign strategy", points: 8 },
      { name: "Competitor analysis", points: 8 },
      { name: "Brand messaging review", points: 8 },
      { name: "90-day growth roadmap", points: 13 },
      { name: "Full go-to-market plan", points: 21 },
    ],
  },
  {
    id: "design",
    name: "Design and creative",
    items: [
      { name: "Basic graphic design asset", points: 2 },
      { name: "Ad creative set", points: 5 },
      { name: "Social media graphic set", points: 5 },
      { name: "Brand moodboard", points: 5 },
      { name: "One-page flyer or brochure", points: 8 },
      { name: "Mini brand identity", points: 21 },
      { name: "Sales deck or pitch deck", points: 21, display: "21–34 pts" },
      { name: "Full brand identity", points: 55, display: "55+ pts" },
    ],
  },
  {
    id: "content",
    name: "Content marketing",
    items: [
      { name: "Short blog post", points: 5 },
      { name: "Long-form SEO blog post", points: 8 },
      { name: "Thought leadership article", points: 13 },
      { name: "Blog refresh", points: 3 },
      { name: "Case study", points: 13 },
      { name: "Lead magnet or guide", points: 21 },
    ],
  },
  {
    id: "social",
    name: "Social media",
    items: [
      { name: "Static social post", points: 1 },
      { name: "Designed carousel", points: 3 },
      { name: "Short-form video edit", points: 5 },
      { name: "Monthly social calendar", points: 8 },
      { name: "12 social posts", points: 13 },
      { name: "20 social posts", points: 21 },
    ],
  },
  {
    id: "video",
    name: "Video editing",
    items: [
      { name: "Raw footage review and selects", points: 2 },
      { name: "Single short-form Reel or TikTok edit, up to 60 seconds", points: 5 },
      { name: "Short-form pack: 3 reels from one shoot", points: 8 },
      { name: "Captions and subtitles pass", points: 3, display: "3 pts / video" },
      { name: "Talking-head long-form edit, 5–15 minutes", points: 13 },
      { name: "Podcast episode edit, audio and video, up to 60 minutes", points: 21 },
      { name: "YouTube long-form edit with B-roll and graphics", points: 21 },
      { name: "Highlight reel or sizzle reel", points: 13 },
      { name: "Testimonial or case-study video edit", points: 13 },
      { name: "Sales VSL edit", points: 34 },
      { name: "Course or lesson video edit", points: 8, display: "8 pts / module" },
      { name: "Monthly short-form retainer: 12 reels", points: 55 },
    ],
  },
  {
    id: "motion",
    name: "Motion graphics",
    items: [
      { name: "Animated logo sting or bumper, 3–5 seconds", points: 5 },
      { name: "Lower-thirds and title-card pack", points: 5 },
      { name: "Kinetic typography clip, up to 30 seconds", points: 8 },
      { name: "Social ad animation, one 15-second spot", points: 8 },
      { name: "Animated infographic", points: 13 },
      { name: "Product feature animation, up to 30 seconds", points: 13 },
      { name: "Explainer animation script and storyboard", points: 13 },
      { name: "Short animated explainer, 30–60 seconds", points: 34 },
      { name: "Full animated explainer, 60–90 seconds", points: 55 },
      { name: "Animated end-screen or outro pack", points: 5 },
      { name: "Branded motion-graphics template kit", points: 21 },
      { name: "UI motion or app-demo animation", points: 21 },
    ],
  },
  {
    id: "seo",
    name: "SEO",
    items: [
      { name: "Keyword research", points: 3 },
      { name: "SEO page optimization", points: 3, display: "3 pts / page" },
      { name: "Blog SEO optimization", points: 2, display: "2 pts / article" },
      { name: "Local SEO setup", points: 13 },
      { name: "Technical SEO mini-audit", points: 8 },
      { name: "Full SEO audit", points: 21 },
    ],
  },
  {
    id: "paid-ads",
    name: "Paid ads",
    items: [
      { name: "Ad-account audit", points: 8 },
      { name: "Simple campaign setup", points: 13 },
      { name: "Complex campaign setup", points: 21 },
      { name: "Ad-copy set", points: 5 },
      { name: "Creative-testing plan", points: 8 },
      { name: "Monthly ad management", points: 21 },
      { name: "Conversion-tracking setup", points: 13 },
    ],
  },
  {
    id: "email",
    name: "Email marketing",
    items: [
      { name: "Single email campaign", points: 3 },
      { name: "Three-email sequence", points: 8 },
      { name: "Five-email sequence", points: 13 },
      { name: "Welcome automation", points: 13 },
      { name: "Lead-nurture automation", points: 21 },
      { name: "Monthly email management", points: 13 },
    ],
  },
  {
    id: "web",
    name: "Web and landing pages",
    items: [
      { name: "Website update", points: 2, display: "2–8 pts" },
      { name: "Landing-page wireframe", points: 5 },
      { name: "Landing-page copy", points: 8 },
      { name: "Landing-page design", points: 13 },
      { name: "Landing-page build", points: 13 },
      { name: "Full landing page", points: 34 },
      { name: "Website audit", points: 13 },
      { name: "CRO audit", points: 13 },
      { name: "Five-page website design and build", points: 55, display: "55–89 pts" },
    ],
  },
  {
    id: "analytics",
    name: "Analytics and reporting",
    items: [
      { name: "Monthly report", points: 3 },
      { name: "Performance-review call", points: 2 },
      { name: "Dashboard setup", points: 13 },
      { name: "GA4 audit", points: 8 },
      { name: "Funnel analysis", points: 13 },
      { name: "Quarterly strategy review", points: 8 },
    ],
  },
  {
    id: "ai",
    name: "AI tasks",
    items: [
      { name: "AI content-idea generation", points: 2 },
      { name: "AI-assisted blog outline", points: 3 },
      { name: "AI-assisted first draft with human editing", points: 8 },
      { name: "AI prompt-library creation", points: 13 },
      { name: "Custom AI workflow design", points: 21 },
      { name: "AI content-repurposing system", points: 13 },
      { name: "AI chatbot planning", points: 13 },
      { name: "AI chatbot setup support", points: 34 },
      { name: "AI lead-qualification flow", points: 34 },
      { name: "AI customer-support workflow", points: 55 },
      { name: "AI social-media caption system", points: 13 },
      { name: "AI email-draft workflow", points: 13 },
      { name: "AI internal SOP creation", points: 21 },
      { name: "AI automation strategy session", points: 5 },
      { name: "AI tool research and recommendation", points: 8 },
    ],
  },
  {
    id: "automation",
    name: "Automation tasks",
    items: [
      { name: "Automation strategy session", points: 5 },
      { name: "Workflow audit", points: 8 },
      { name: "Simple Zapier or Make automation", points: 8 },
      { name: "Multi-step automation", points: 21 },
      { name: "Lead-notification automation", points: 8 },
      { name: "Form-to-CRM automation", points: 13 },
      { name: "Email-list segmentation setup", points: 13 },
      { name: "CRM pipeline setup", points: 21 },
      { name: "Appointment-booking workflow", points: 13 },
      { name: "Client-onboarding automation", points: 34 },
      { name: "Review-request automation", points: 13 },
      { name: "Reporting automation", points: 21 },
      { name: "AI plus automation workflow", points: 55 },
      { name: "Automation troubleshooting", points: 8 },
      { name: "Automation documentation or SOP", points: 13 },
    ],
  },
];

/**
 * Worked example allocations, one per plan.
 *
 * Each `item` name must exist in CATALOG and each list must sum to its plan's
 * monthly points — scripts/test_growth_points.mjs asserts both, so an example
 * cannot quietly drift away from the catalog it is illustrating.
 */
export const EXAMPLE_ALLOCATIONS = [
  {
    planId: "starter-growth",
    title: "Quick Momentum Month",
    items: [
      { name: "Monthly marketing plan", points: 5 },
      { name: "Brand messaging review", points: 8 },
      { name: "Three-email sequence", points: 8 },
      { name: "Basic graphic design asset", points: 4, note: "×2" },
    ],
  },
  {
    planId: "momentum",
    title: "Lead Conversion Month",
    items: [
      { name: "Campaign strategy", points: 8 },
      { name: "Landing-page copy", points: 8 },
      { name: "Landing-page design", points: 13 },
      { name: "Landing-page build", points: 13 },
      { name: "Monthly report", points: 3 },
    ],
  },
  {
    planId: "scale-engine",
    title: "Growth System Month",
    items: [
      { name: "90-day growth roadmap", points: 13 },
      { name: "Full landing page", points: 34 },
      { name: "Conversion-tracking setup", points: 13 },
      { name: "Ad-copy set", points: 5 },
      { name: "Creative-testing plan", points: 8 },
      { name: "Performance-review call", points: 2 },
    ],
  },
  {
    planId: "authority",
    title: "Authority and Automation Month",
    items: [
      { name: "Thought leadership article", points: 13 },
      { name: "Lead magnet or guide", points: 21 },
      { name: "Lead-nurture automation", points: 21 },
      { name: "AI content-repurposing system", points: 13 },
      { name: "Multi-step automation", points: 21 },
      { name: "Dashboard setup", points: 13 },
      { name: "Quarterly strategy review", points: 8 },
    ],
  },
];

/** The thirteen disciplines points can be spent across. */
export const CAPABILITIES = [
  "Strategy and planning",
  "Design and creative",
  "Content marketing",
  "Social media",
  "Video editing",
  "Motion graphics",
  "SEO",
  "Paid ads",
  "Email marketing",
  "Web and landing pages",
  "Analytics and reporting",
  "AI tasks",
  "Automation tasks",
];

/**
 * The investment bands the application offers, mapped to the plan each one
 * can actually afford.
 *
 * `planId: null` means the band cannot fund any recurring plan — those
 * applicants are steered toward a one-off block or a resource rather than a
 * plan they would have to be talked out of on the call.
 *
 * A band maps to the best plan its FLOOR can afford, not its ceiling. The
 * $10,000–$14,999 band is the one that makes the difference: Authority is
 * $11,000/mo, so an applicant at $10,000 who was routed there would be
 * recommended a plan they had just said they could not afford. They get Scale
 * Engine, and the Growth Allocation Call can move them up if their real
 * number is higher. Recommending down and upgrading on a call is recoverable;
 * recommending up is the kind of thing that ends the conversation.
 *
 * Kept beside `priceMonthly` on purpose: the mapping is only correct as long
 * as the two agree, and scripts/test_growth_points.mjs checks that every
 * non-null band actually covers its plan's price.
 */
export const INVESTMENT_BANDS = [
  { value: "under_2500",  label: "Under $2,500",      minMonthly: 0,     planId: null },
  { value: "2500_4999",   label: "$2,500–$4,999",     minMonthly: 2500,  planId: "starter-growth" },
  { value: "5000_7499",   label: "$5,000–$7,499",     minMonthly: 5000,  planId: "momentum" },
  { value: "7500_9999",   label: "$7,500–$9,999",     minMonthly: 7500,  planId: "scale-engine" },
  { value: "10000_14999", label: "$10,000–$14,999",   minMonthly: 10000, planId: "scale-engine" },
  { value: "15000_plus",  label: "$15,000+",          minMonthly: 15000, planId: "authority" },
  { value: "need_pricing", label: "I need pricing before I can decide", minMonthly: null, planId: null },
];

/** Operating rules, shown on the sales page and the catalog. */
export const OPERATING_RULES = [
  {
    title: "Points are not hours",
    body: "They represent value, strategy, complexity, effort, and production capacity.",
  },
  {
    title: "Points reset monthly",
    body: "Unused points reset unless rollover is included in writing.",
  },
  {
    title: "Extra work requires extra points",
    body: "New work can replace queued work that has not started, use an add-on block, or move to the next month.",
  },
  {
    title: "Rush work can require more points",
    body: "Rush requests may require 1.5–2.5 times the standard points depending on deadline and availability.",
  },
  {
    title: "External costs are separate",
    body: "Software, subscriptions, ad spend, hosting, domains, premium plugins, APIs, AI tools, and third-party platform fees are excluded unless stated in writing.",
  },
  {
    title: "Client readiness affects delivery",
    body: "Work begins when Bow Tie receives the required access, assets, brief, decisions, and approvals.",
  },
];

/** Total number of catalog line items, for the "N deliverables" copy. */
export const CATALOG_ITEM_COUNT = CATALOG.reduce((n, g) => n + g.items.length, 0);

export function planById(id) {
  return PLANS.find((p) => p.id === id) || null;
}

export function allocationTotal(allocation) {
  return allocation.items.reduce((n, i) => n + i.points, 0);
}
